import React, { useEffect, useRef, useState } from 'react';
import PolicyImpactTool from '../tools/PolicyImpactTool';
import { useLocation } from '../../contexts/LocationContext';
import { PageHeader } from '../layout/PageHeader';
import { useAdminPageSettings } from '../../hooks/useAdminPageSettings';
import { isVMTDataAvailable, getVMTDataForGHG } from '../../services/vmtDataService';
import { clearBaselineCache } from '../../services/policyBaselineCache';

/** Max wait for VMT before showing tool (then baseline fetches VMT if needed). Match Transportation page load time (~10s). */
const VMT_READY_TIMEOUT_MS = 10000;

export const PolicyTool: React.FC = () => {
  const { selectedLocation, selectedGeofips, coreDataLoading, vmtReady } = useLocation();
  const { isSectionEnabled, getSectionOrder } = useAdminPageSettings('policy_tool', selectedLocation);
  const [vmtWaitExpired, setVmtWaitExpired] = useState(false);
  const hadVMTWhenShownRef = useRef<boolean>(false);

  const sectionStyle = {
    order: getSectionOrder('policy_impact_tool'),
    display: isSectionEnabled('policy_impact_tool') ? 'block' : 'none'
  };

  // Ensure VMT is requested when Policy Tool mounts with a location (so GHG inventory gets LDV/HDV even if Transportation page wasn't loaded first).
  useEffect(() => {
    if (!selectedLocation || !selectedGeofips || isVMTDataAvailable()) return;
    getVMTDataForGHG(selectedLocation, selectedGeofips).catch(() => {});
  }, [selectedLocation, selectedGeofips]);

  // Loading order: VMT (Transportation) → GHG inventory → Policy tool. Wait for VMT; poll so we show as soon as it's set.
  useEffect(() => {
    if (coreDataLoading || !selectedLocation) {
      setVmtWaitExpired(false);
      return;
    }
    if (vmtReady || isVMTDataAvailable()) {
      setVmtWaitExpired(true);
      return;
    }
    const timeoutId = setTimeout(() => setVmtWaitExpired(true), VMT_READY_TIMEOUT_MS);
    const pollId = setInterval(() => {
      if (isVMTDataAvailable()) setVmtWaitExpired(true);
    }, 400);
    return () => {
      clearTimeout(timeoutId);
      clearInterval(pollId);
    };
  }, [coreDataLoading, selectedLocation, vmtReady]);

  const canShowTool = !!selectedLocation && !coreDataLoading && (vmtReady || isVMTDataAvailable() || vmtWaitExpired);
  const hasVMT = isVMTDataAvailable();

  // Track whether we had VMT when we first showed the tool (reset when location changes).
  useEffect(() => {
    if (!selectedLocation) {
      hadVMTWhenShownRef.current = false;
      return;
    }
    if (!canShowTool) return;
    if (hasVMT) hadVMTWhenShownRef.current = true;
  }, [selectedLocation, canShowTool, hasVMT]);

  // If we're showing without VMT (timeout), clear baseline so the first calculation fetches VMT and gets LDV/HDV.
  useEffect(() => {
    if (!canShowTool || !selectedLocation) return;
    if (!vmtWaitExpired || hasVMT) return;
    clearBaselineCache(selectedLocation);
  }, [canShowTool, selectedLocation, vmtWaitExpired, hasVMT]);

  // When we gain VMT after showing without it (e.g. user visited Transport and came back), clear baseline so chart refetches with LDV/HDV.
  useEffect(() => {
    if (!canShowTool || !selectedLocation || !hasVMT) return;
    if (hadVMTWhenShownRef.current) return;
    clearBaselineCache(selectedLocation);
    hadVMTWhenShownRef.current = true;
  }, [canShowTool, selectedLocation, hasVMT]);

  if (!selectedLocation || !canShowTool) {
    return (
      <div>
        <PageHeader
          title="GHG Policy Impact Tool (Under development)"
          variant="maps"
        />
        <div className="page-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{coreDataLoading ? `Loading core data for ${selectedLocation}...` : 'Loading transport data (VMT) for GHG inventory (LDV/HDV)...'}</p>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: 8 }}>
              {coreDataLoading ? 'Typically 5–15 seconds (VMT + buildings).' : 'Typically under 10 seconds; tool will appear within 10 seconds.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="GHG Policy Impact Tool (Under development)"
        variant="maps" // reuse the purple header styling
      />

      <div className="page-container">
        <div style={sectionStyle}>
          <PolicyImpactTool location={selectedLocation} geofips={selectedGeofips} />
        </div>
      </div>
    </div>
  );
};
